import React, { useEffect, useState } from "react";
import { useLoader } from "../context/LoaderContext";
import { useLocation } from "react-router-dom";
import {
  IBannerData,
  ITestimonialData,
  ITestimonialInfoData,
} from "../exports/interfaces";
import { fetchTestimonial, fetchTestimonialInfo } from "../api/TestimonialApi";
import { dynamicFailed } from "../hooks/custom/useToast";
import Banner from "../components/Banner";
import { fetchTestimonialsBanner } from "../api/BannerApi";

const TestimonialPage = () => {
  const { setLoading } = useLoader();
  const location = useLocation();

  const [testimonialDetails, setTestimonialDetails] = useState<
    ITestimonialData[]
  >([]);
  const [testimonialInfoDetails, setTestimonialInfoDetails] = useState<
    ITestimonialInfoData[]
  >([]);
  const [testimonialBanner, setTestimonialBanner] = useState<IBannerData[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);
  const [visibleCards, setVisibleCards] = useState(3);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchTestimonial(),
      fetchTestimonialInfo(),
      fetchTestimonialsBanner(),
    ])
      .then(
        ([
          testimonialDetailsData,
          testimonialInfoData,
          testimonialsBannerData,
        ]) => {
          setTestimonialDetails(testimonialDetailsData);
          setTestimonialInfoDetails(testimonialInfoData);
          setTestimonialBanner(testimonialsBannerData);
        },
      )
      .catch((error) => {
        dynamicFailed(error.response.data.error.message);
      })
      .finally(() => {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      });
  }, [setLoading]);

  const testimonialItems = testimonialDetails ?? [];
  const testimonialInfoItems = testimonialInfoDetails ?? [];
  const bannerItems = testimonialBanner ?? [];

  useEffect(() => {
    const updateVisibleCards = () => {
      const width = window.innerWidth;
      if (width < 768) {
        // Mobile: 1 card
        setVisibleCards(1);
      } else if (width < 1024) {
        // Tablet: 2 cards
        setVisibleCards(2);
      } else {
        // Desktop: 3 cards
        setVisibleCards(3);
      }
    };

    updateVisibleCards();
    window.addEventListener("resize", updateVisibleCards);

    return () => window.removeEventListener("resize", updateVisibleCards);
  }, []);

  useEffect(() => {
    if (testimonialItems.length <= 1 || hoveredCardId !== null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % testimonialItems.length);
    }, 5000);

    return () => window.clearInterval(intervalId);
  }, [testimonialItems.length, hoveredCardId]);

  const handlePrevious = () => {
    if (testimonialItems.length === 0 || isAnimating) {
      return;
    }

    setIsAnimating(true);
    setActiveIndex((prevIndex) =>
      prevIndex === 0 ? testimonialItems.length - 1 : prevIndex - 1,
    );
    window.setTimeout(() => setIsAnimating(false), 450);
  };

  const handleNext = () => {
    if (testimonialItems.length === 0 || isAnimating) {
      return;
    }

    setIsAnimating(true);
    setActiveIndex((prevIndex) => (prevIndex + 1) % testimonialItems.length);
    window.setTimeout(() => setIsAnimating(false), 450);
  };

  const visibleTestimonials =
    testimonialItems.length > 0
      ? [...testimonialItems, ...testimonialItems.slice(0, 2)]
      : [];

  return (
    <>
      <Banner title="Testimonials" bannerImage={bannerItems?.[0]?.image?.[0]} />

      <section>
        <div className="td_height_120 td_height_lg_40" />
        {/* <div className="td_height_120 td_height_lg_40" /> */}

        <div className="container">
          <div
            className="td_section_heading td_style_1 text-center wow fadeInUp"
            data-wow-duration="1s"
            data-wow-delay="0.15s"
          >
            {/* <p
              className="td_section_subtitle_up td_fs_18 td_semibold td_spacing_1 td_mb_10 text-uppercase td_accent_color"
              style={{ textAlign: "unset" }}
            >
              COMMUNITY
            </p> */}

            <h2 className="td_section_title td_fs_48 mb-0">
              {testimonialInfoItems[0]?.title}
            </h2>

            <div className="td_height_30 td_height_lg_30" />
          </div>

          <p
            className="td_fs_18 mb-0"
            dangerouslySetInnerHTML={{
              __html: testimonialInfoItems[0]?.header_text,
            }}
          />

          <div className="td_height_30 td_height_lg_30" />

          <div className="mb-4" style={{ overflow: "hidden" }}>
            <div className="position-relative">
              <div
                style={{
                  display: "flex",
                  transition: "transform 0.45s ease-in-out",
                  transform: `translateX(calc(-${activeIndex * (100 / visibleCards)}% - ${activeIndex * 16}px))`,
                  width: "100%",
                  gap: "1rem",
                }}
              >
                {visibleTestimonials?.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className="wow fadeInUp"
                    data-wow-duration="1s"
                    data-wow-delay="0.4s"
                    data-testid="testimonial-card"
                    style={{
                      flex: `0 0 calc((100% - ${(visibleCards - 1) * 16}px) / ${visibleCards})`,
                      minWidth: 0,
                    }}
                  >
                    <div
                      className="h-100"
                      onMouseEnter={() => setHoveredCardId(item.id)}
                      onMouseLeave={() => setHoveredCardId(null)}
                      style={{
                        background: "#ffffff",
                        border: "1px solid rgba(0, 0, 0, 0.08)",
                        borderRadius: "20px",
                        padding: "1.5rem",
                        minHeight: "260px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        boxShadow:
                          hoveredCardId === item.id
                            ? "0 10px 24px rgba(15, 23, 42, 0.07)"
                            : "none",
                        transition:
                          "transform 0.25s ease, box-shadow 0.25s ease",
                        transform:
                          hoveredCardId === item.id
                            ? "translateY(-4px)"
                            : "translateY(0)",
                        overflowY:
                          hoveredCardId === item.id ? "auto" : "hidden",
                        scrollbarWidth: "thin",
                        scrollbarColor: "rgba(0,0,0,0.2) transparent",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            color: "#243238",
                            fontSize: "2rem",
                            lineHeight: 1,
                            marginBottom: "0.75rem",
                          }}
                        >
                          “
                        </div>
                        <p
                          className="td_fs_18 mb-0"
                          style={{ color: "#4b5563", lineHeight: 1.7 }}
                          dangerouslySetInnerHTML={{
                            __html: item?.description,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex justify-content-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="btn btn-light"
                  aria-label="Previous"
                  style={{
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    color: "#243238",
                  }}
                >
                  ←
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn btn-light"
                  aria-label="Next"
                  style={{
                    borderRadius: "50%",
                    width: "44px",
                    height: "44px",
                    border: "1px solid rgba(0, 0, 0, 0.08)",
                    color: "#243238",
                  }}
                >
                  →
                </button>
              </div>
            </div>
          </div>

          <div className="td_height_30 td_height_lg_30" />

          <p
            className="td_fs_18 mb-0"
            dangerouslySetInnerHTML={{
              __html: testimonialInfoItems[0]?.footer_text,
            }}
          />
        </div>
      </section>
    </>
  );
};

export default TestimonialPage;
